import React, { useState, useEffect } from 'react';
import { 
  HardDrive, 
  Trash2, 
  AlertTriangle, 
  CheckCircle, 
  Loader, 
  Info,
  Calendar,
  Server
} from 'lucide-react';
import styles from './ManageServersModal.module.css';

function StorageManager({ onError, appType = 'jupyterlab' }) {
  const [storageInfo, setStorageInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    fetchStorageInfo();
  }, [appType]);

  const fetchStorageInfo = async () => {
    setLoading(true);
    setError('');
    try {
      const endpoint = '/v1/efs_storage/info';
      const response = await fetch(endpoint, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data.RetCode === 0) {
        setStorageInfo(data.data);
      } else {
        setError(data.retmsg || 'Failed to fetch storage info');
        onError?.(data.retmsg || 'Failed to fetch storage info');
      }
    } catch (err) {
      setError('Failed to fetch storage info. Please try again.');
      onError?.('Failed to fetch storage info. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  const deleteStorage = async () => {
    if (!window.confirm('Are you sure you want to delete your persistent storage? This will permanently delete all your data and cannot be undone.')) {
      return;
    }

    setActionLoading((prev) => ({ ...prev, delete: true }));
    setError('');
    try {
      const endpoint = '/v1/efs_storage/cleanup';
      const response = await fetch(endpoint, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data.RetCode === 0) {
        await fetchStorageInfo();
      } else {
        setError(data.retmsg || 'Failed to delete storage');
        onError?.(data.retmsg || 'Failed to delete storage');
      }
    } catch (err) {
      setError('Failed to delete storage. Please try again.');
      onError?.('Failed to delete storage. Please try again.');
    } finally {
      setActionLoading((prev) => ({ ...prev, delete: false }));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Bound': return 'text-green-500';
      case 'Pending': return 'text-yellow-500';
      case 'Failed': return 'text-red-500';
      case 'not_found': return 'text-gray-500';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Bound': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'Pending': return <Loader className="h-4 w-4 text-yellow-500 animate-spin" />;
      case 'Failed': return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'not_found': return <Info className="h-4 w-4 text-gray-500" />;
      default: return <Info className="h-4 w-4 text-muted-foreground" />;
    }
  };



  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="flex-1 min-h-0">
      {/* Header */}
      <div className={`${styles.serverSectionHeader} mb-6`}>
        <h3 className="text-xl font-semibold text-foreground">Storage Management</h3>
        <button
          onClick={() => fetchStorageInfo()}
          disabled={loading}
          className="px-3 py-1 rounded-md bg-muted text-muted-foreground hover:bg-muted/80 transition-colors flex items-center disabled:opacity-50"
        >
          {loading ? (
            <Loader className="h-4 w-4 animate-spin" />
          ) : (
            'Refresh'
          )}
        </button>
      </div>

      {/* Storage Info */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader className="h-6 w-6 animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground">Loading storage info...</span>
          </div>
        ) : !storageInfo?.exists ? (
          <div className="text-center py-8 text-muted-foreground">
            <HardDrive className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No persistent storage found.</p>
            <p className="text-sm">Storage will be created automatically when you create your first server.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Storage Card */}
            <div className={styles.storageCard}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(storageInfo.status)}
                  <div>
                    <h4 className="font-medium text-foreground">Persistent Storage</h4>
                    <p className={`text-sm ${getStatusColor(storageInfo.status)}`}>
                      {storageInfo.status === 'not_found' ? 'Not Found' : storageInfo.status}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={deleteStorage}
                    disabled={actionLoading.delete || storageInfo.server_count > 0}
                    className={`${styles.serverActionButton} ${styles.delete}`}
                    title={storageInfo.server_count > 0 ? "Cannot delete storage while servers exist" : "Delete Storage"}
                  >
                    {actionLoading.delete ? (
                      <Loader className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Storage Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <HardDrive className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Usage:</span>
                  <span className="font-medium">{storageInfo.capacity || 'Unknown'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Server className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Total Servers:</span>
                  <span className="font-medium">{storageInfo.server_count}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Created:</span>
                  <span className="font-medium">{formatDate(storageInfo.created_at)}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Info className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Type:</span>
                  <span className="font-medium">{storageInfo.storage_type || 'Persistent Storage'}</span>
                </div>
              </div>

              {/* Warning if servers exist */}
              {storageInfo.server_count > 0 && (
                <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                  <p className="text-xs text-yellow-700 dark:text-yellow-300">
                    ⚠️ Cannot delete storage while {storageInfo.server_count} server{storageInfo.server_count !== 1 ? 's exist' : ' exists'} (running or stopped). 
                    Delete all servers first to delete storage.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

export default StorageManager; 